import { User } from 'oidc-client-ts';
import { useAuth0, Auth0ContextInterface, User as Auth0User } from '@auth0/auth0-react';
import { useAuth, AuthContextProps } from 'react-oidc-context';

export enum AuthHookEnum {
  Auth0,
  Authentik,
}

export interface AuthorizationProcedures {
  getSessionId: () => Promise<unknown>;
  logout: (identityproviderLogOut: boolean) => Promise<void>;
}

/*
 * Return the correct hook function from the assigned identity provider :
 * - Auth0 : useAuth0
 *  - OIDC : useAuth
 * The hook is not invoked yet.
 */
export const getIdentityProviderHook = (authEnum: AuthHookEnum): (() => AuthContextProps | Auth0ContextInterface) => {
  switch (authEnum) {
    case AuthHookEnum.Auth0:
      return useAuth0;

    case AuthHookEnum.Authentik:
      return useAuth;

    default:
      throw new Error('Identity provider not selected');
  }
};

/* Double check that two things :
 * - The chosen hook is the desired hook
 * - The chosen hook has not changed interfaces
 */
export const validateChosenHook = (
  authEnum: AuthHookEnum,
  oidcAuth: Auth0ContextInterface<Auth0User> | AuthContextProps,
): void => {
  const err = (interface_name: string, provider: string) => {
    return `Chosen interface '${interface_name}' does not match chosen identity provider '${provider}'.`;
  };

  switch (authEnum) {
    case AuthHookEnum.Auth0: {
      const isAuth0 = (oidcAuth as Auth0ContextInterface).getAccessTokenSilently;
      if (isAuth0 === undefined) {
        throw new Error(err('Auth0ContextInterface', 'Auth0'));
      }
      break;
    }
    case AuthHookEnum.Authentik: {
      const isAuthentik = (oidcAuth as AuthContextProps).signinSilent;
      if (isAuthentik === undefined) {
        throw new Error(err('AuthContextProps', 'Authentik'));
      }
      break;
    }

    default: {
      throw new Error('Chosen interface does not match with any identity providers.');
    }
  }
};

/*
 * Function specific to retrieve a user access token according to
 * the react-oidc-context library.
 * The access key is a field of the returned user
 * https://github.com/authts/react-oidc-context?tab=readme-ov-file#call-a-protected-api
 * NB: key-value pair gets stored in session storage, not local storage
 */
export function getUserFromOidcClient(authority: string, clientId: string): Promise<User> {
  const oidcUserSessionStorage = `oidc.user:${authority}:${clientId}`;
  const oidcStorage = sessionStorage.getItem(oidcUserSessionStorage);
  if (!oidcStorage) {
    return Promise.reject({
      response: {
        status: 404,
        message: `Successful login by user not found in current browser session: ${oidcUserSessionStorage}`,
      },
    });
  }

  return Promise.resolve(User.fromStorageString(oidcStorage));
}
